// src/pages/About/AboutPage.tsx
// Modern, Google-style About page — full platform coverage, rounded-[32px] design system

import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import { seoConfig } from '../../config/seo';
import {
    Shield, Zap, Users, Heart,
    CheckCircle, Globe, Lightbulb, ArrowRight,
    Code2, Cpu, LucideIcon,
    Lock, Mail
} from 'lucide-react';
import { m } from 'framer-motion';
import SecurityBadge from '../../components/Shared/SecurityBadge/SecurityBadge';
import { Typography } from '../../components/ui/typography';

// ── Types ─────────────────────────────────────────────────────────────────────

interface ValueCard {
    icon: LucideIcon;
    color: string;
    bg: string;
    title: string;
    description: string;
}

interface TeamCard {
    icon: LucideIcon;
    color: string;
    bg: string;
    role: string;
    label: string;
    description: string;
}

// ── Data ──────────────────────────────────────────────────────────────────────

const values: ValueCard[] = [
    {
        icon: Shield,
        color: '#1a73e8',
        bg: '#e8f0fe',
        title: 'Security & Transparency',
        description:
            'We are honest about our process. Most tools run entirely locally in your browser. Those requiring server power use SSL-encrypted processing with instant deletion — documented fully in our Privacy Policy.',
    },
    {
        icon: Zap,
        color: '#1e8e3e',
        bg: '#e6f4ea',
        title: 'Speed Without Compromise',
        description:
            'Browser-based architecture delivers results instantly for most operations. Adaptive algorithms handle large files using chunked processing and hardware acceleration where supported.',
    },
    {
        icon: Users,
        color: '#f9ab00',
        bg: '#fef7e0',
        title: 'Accessible to Everyone',
        description:
            'Professional tools should not require professional training. We design intuitive interfaces that work for beginners and experts alike — across every device and every browser.',
    },
    {
        icon: Heart,
        color: '#d93025',
        bg: '#fce8e6',
        title: 'Completely Free',
        description:
            'No freemium tier, no paywalled features, no usage caps. Every tool, service, and article on FileFusion is 100% free, forever — for personal and commercial use.',
    },
];

const teamCards: TeamCard[] = [
    {
        icon: Cpu,
        color: '#1a73e8',
        bg: '#e8f0fe',
        role: 'Technical Leadership',
        label: 'Software Architects',
        description:
            'Lead architects specialising in high-performance browser processing (WebAssembly), secure backend scaling, and low-latency API design.',
    },
    {
        icon: Shield,
        color: '#1e8e3e',
        bg: '#e6f4ea',
        role: 'Security & Privacy',
        label: 'SecOps Specialists',
        description:
            'Dedicated specialists ensuring every tool adheres to strict deletion protocols, end-to-end encryption standards, and privacy regulations (GDPR, CCPA, DPDPA).',
    },
    {
        icon: Code2,
        color: '#9334e6',
        bg: '#f3e8fd',
        role: 'Developer Services',
        label: 'Platform Engineers',
        description:
            'Engineers building the Developer Hub — stateless sandbox infrastructure for mock APIs, webhook testing, email capture, and payment simulation.',
    },
];

const AboutPage: React.FC = () => {
    return (
        <>
            <SEO {...seoConfig.about} />

            <div className="min-h-screen bg-white font-brand text-[#202124]">

                {/* ── Hero ───────────────────────────────────────────────────── */}
                <header className="max-w-7xl mx-auto px-6 pt-16 pb-20 text-center">
                    <Typography variant="hero-title" as="h1" weight="light" className="mb-6 text-[#121317]">
                        Built for everyone.{' '}
                        <span className="text-[#1a73e8]">Trusted by professionals.</span>
                    </Typography>
                    <Typography variant="hero-subtitle" weight="normal" className="max-w-2xl mx-auto text-[#5f6368] mb-10">
                        FileFusion is a complete professional platform — free tools, developer sandbox services,
                        and engineering education — built on the principle that privacy and power are never a trade-off.
                    </Typography>
                    <div className="flex flex-wrap gap-4 justify-center">
                        <Link
                            to="/tools"
                            className="inline-flex items-center gap-2 px-7 py-3 bg-[#1a73e8] text-white font-medium rounded-full no-underline hover:bg-[#1557b0] transition-colors shadow-sm"
                        >
                            Explore Tools <ArrowRight size={17} />
                        </Link>
                        <Link
                            to="/features"
                            className="inline-flex items-center gap-2 px-7 py-3 bg-white border border-[#dadce0] text-[#3c4043] font-medium rounded-full no-underline hover:bg-[#f8f9fa] transition-colors"
                        >
                            See All Features
                        </Link>
                    </div>
                </header>

                {/* ── Mission Strip ──────────────────────────────────────────── */}
                <section className="max-w-7xl mx-auto px-6 pb-20 border-b border-[#f1f3f4]">
                    <div className="flex justify-center gap-12 sm:gap-24 flex-wrap">
                        {[
                            { value: '8', label: 'Professional Tools' },
                            { value: '4', label: 'Developer Services' },
                            { value: '0', label: 'Sign-ups Required' },
                            { value: '100%', label: 'Free Forever' },
                        ].map(s => (
                            <div key={s.label} className="flex flex-col items-center text-center">
                                <div className="text-[2.75rem] font-light text-[#121317] leading-none mb-2">{s.value}</div>
                                <div className="text-[0.875rem] text-[#5f6368]">{s.label}</div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* ── Our Story ──────────────────────────────────────────────── */}
                <section className="max-w-7xl mx-auto px-6 py-20">
                    <div className="bg-[#f8f9fa] rounded-[32px] p-8 md:p-16">
                        <div>
                            <span className="text-[0.75rem] font-semibold uppercase tracking-widest text-[#1a73e8] mb-4 block">Our Story</span>
                            <h2 className="text-3xl md:text-4xl font-light text-[#121317] mb-8">
                                Privacy and power shouldn't be a trade-off
                            </h2>
                            <div className="space-y-5 text-[0.9375rem] text-[#5f6368] leading-[1.8]">
                                <p className="text-[1.05rem] text-[#3c4043]">
                                    FileFusion was born from a simple observation: developers and professionals needed to compare
                                    sensitive text and code, but every existing online tool uploaded everything to remote servers —
                                    a significant security risk with no good reason to exist.
                                </p>
                                <p>
                                    We built the first privacy-first text comparison engine using WebAssembly and client-side
                                    processing — performing complex diff operations entirely in the browser without a single byte
                                    leaving the user's device. What began as a secure diff checker quickly became something larger.
                                </p>
                                <p>
                                    Today, FileFusion is a complete professional platform: eight browser-based tools, a developer
                                    sandbox hub with four infrastructure services (Mock API, Webhook Tester, Email Sandbox, Payment
                                    Simulator), and a knowledge platform with engineering articles and structured programming courses —
                                    all free, all private, all without sign-up.
                                </p>
                                <p>
                                    Our users span every discipline — developers using the Dev Hub to prototype integrations,
                                    designers removing backgrounds for marketing assets, legal teams comparing contract revisions,
                                    students compressing research PDFs, engineers validating JSON configs before deployment.
                                    Our core promise has never changed: your data stays yours.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ── Security Commitment ────────────────────────────────────── */}
                <section className="max-w-7xl mx-auto px-6 pb-20 border-t border-[#f1f3f4] pt-20">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Left: how we handle files */}
                        <m.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5 }}
                            className="bg-white border border-[#e8eaed] rounded-[32px] p-8 md:p-12"
                        >
                            <div className="flex items-center gap-4 mb-8">
                                <div className="w-12 h-12 bg-[#e8f0fe] rounded-2xl flex items-center justify-center">
                                    <Shield size={24} className="text-[#1a73e8]" />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-medium text-[#121317]">How We Handle Your Files</h2>
                                    <p className="text-[0.875rem] text-[#5f6368]">Process it fast, keep it safe, delete it immediately</p>
                                </div>
                            </div>
                            <div className="space-y-5 mb-8">
                                {[
                                    { n: '1', title: 'Secure Transit (SSL/TLS)', desc: 'All files transmitted to our servers are encrypted using industry-standard TLS. No plain-text transfer at any point.' },
                                    { n: '2', title: 'Memory-Only Processing', desc: 'Files are processed in RAM (volatile memory). We never write your raw data to permanent storage disks. Browser-based tools never upload at all.' },
                                    { n: '3', title: 'Instant Automatic Deletion', desc: 'The moment results are generated, source files are wiped from our memory nodes permanently. No logs, no backups, no traces.' },
                                ].map(step => (
                                    <div key={step.n} className="flex gap-4">
                                        <div className="w-8 h-8 bg-[#e8f0fe] text-[#1a73e8] rounded-full flex items-center justify-center font-semibold shrink-0 text-sm">
                                            {step.n}
                                        </div>
                                        <div>
                                            <h4 className="text-[1rem] font-medium text-[#121317] mb-1">{step.title}</h4>
                                            <p className="text-[0.875rem] text-[#5f6368] leading-relaxed">{step.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="flex flex-wrap gap-3">
                                <SecurityBadge type="local" />
                                <SecurityBadge type="encrypted" />
                                <SecurityBadge type="deleted" />
                                <SecurityBadge type="private" />
                            </div>
                        </m.div>

                        {/* Right: core values */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {values.map((v, i) => {
                                const Icon = v.icon;
                                return (
                                    <m.div
                                        key={i}
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.45, delay: i * 0.08 }}
                                        className="bg-white border border-[#e8eaed] rounded-[32px] p-6 flex flex-col"
                                    >
                                        <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-5" style={{ backgroundColor: v.bg }}>
                                            <Icon size={22} style={{ color: v.color }} />
                                        </div>
                                        <h3 className="text-[1rem] font-medium text-[#121317] mb-2">{v.title}</h3>
                                        <p className="text-[0.875rem] text-[#5f6368] leading-relaxed">{v.description}</p>
                                    </m.div>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* ── Technology ─────────────────────────────────────────────── */}
                <section className="bg-[#fafafa] border-t border-[#f1f3f4] py-20">
                    <div className="max-w-7xl mx-auto px-6">
                        <div className="mb-12 text-center">
                            <h2 className="text-3xl md:text-4xl font-light text-[#121317] mb-3">Our Technology</h2>
                            <p className="text-[#5f6368] text-lg">The engineering that makes privacy-first tools possible</p>
                        </div>
                        <div className="bg-white border border-[#e8eaed] rounded-[32px] p-8 md:p-12 grid grid-cols-1 sm:grid-cols-2 gap-8">
                            {[
                                {
                                    title: 'Browser-Based Processing',
                                    desc: 'Most tools operate entirely in your browser using WebAssembly and modern JavaScript. Diff, OCR, QR generation, and JSON validation — all run locally with zero upload required.',
                                },
                                {
                                    title: 'Progressive Web App',
                                    desc: 'Built as a PWA — app-like experience with no installation. Many features work offline after initial page load. Adapts seamlessly across desktop, tablet, and mobile.',
                                },
                                {
                                    title: 'Adaptive Algorithms',
                                    desc: 'Chunked processing for large files, hardware acceleration where available, intelligent compression that analyses each file type before applying settings.',
                                },
                                {
                                    title: 'Cross-Platform',
                                    desc: 'Works on every modern browser — Chrome, Firefox, Safari, Edge — across Windows, Mac, Linux, iOS, and Android. No platform-specific builds or installs.',
                                },
                            ].map((t, i) => (
                                <m.div
                                    key={i}
                                    initial={{ opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.45, delay: i * 0.07 }}
                                    className="flex gap-4"
                                >
                                    <CheckCircle size={20} className="text-[#1e8e3e] shrink-0 mt-0.5" />
                                    <div>
                                        <h3 className="text-[1rem] font-medium text-[#121317] mb-1">{t.title}</h3>
                                        <p className="text-[0.875rem] text-[#5f6368] leading-relaxed">{t.desc}</p>
                                    </div>
                                </m.div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ── Team / Expertise ───────────────────────────────────────── */}
                <section className="max-w-7xl mx-auto px-6 py-20 border-t border-[#f1f3f4]">
                    <div className="mb-12 text-center">
                        <h2 className="text-3xl md:text-4xl font-light text-[#121317] mb-3">Our Expertise</h2>
                        <p className="text-[#5f6368] text-lg max-w-xl mx-auto">
                            A small, focused team of engineers, security specialists, and platform builders
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                        {teamCards.map((t, i) => {
                            const Icon = t.icon;
                            return (
                                <m.div
                                    key={i}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.45, delay: i * 0.1 }}
                                    className="bg-white border border-[#e8eaed] rounded-[32px] p-8 text-center flex flex-col items-center"
                                >
                                    <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6" style={{ backgroundColor: t.bg }}>
                                        <Icon size={32} style={{ color: t.color }} />
                                    </div>
                                    <h3 className="text-[1.2rem] font-medium text-[#121317] mb-1">{t.role}</h3>
                                    <p className="text-[0.75rem] font-semibold uppercase tracking-wider mb-4" style={{ color: t.color }}>{t.label}</p>
                                    <p className="text-[0.875rem] text-[#5f6368] leading-relaxed">{t.description}</p>
                                </m.div>
                            );
                        })}
                    </div>
                    {/* Sub-traits */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                        {[
                            { icon: Globe, title: 'Global Perspective', desc: 'Designed for a worldwide audience — tools and content that work across languages, regions, and cultural contexts.' },
                            { icon: Lightbulb, title: 'User-Focused Innovation', desc: 'Every feature solves real problems reported by real users. We listen to feedback and ship improvements continuously.' },
                            { icon: Zap, title: 'Continuous Improvement', desc: "We're constantly learning and refining. Our tools evolve based on the latest web technologies and evolving user needs." },
                        ].map((q, i) => {
                            const Icon = q.icon;
                            return (
                                <div key={i} className="bg-[#f8f9fa] rounded-[32px] p-6 flex flex-col items-center text-center">
                                    <Icon size={22} className="text-[#1a73e8] mb-4" />
                                    <h3 className="text-[1rem] font-medium text-[#121317] mb-2">{q.title}</h3>
                                    <p className="text-[0.875rem] text-[#5f6368] leading-relaxed">{q.desc}</p>
                                </div>
                            );
                        })}
                    </div>
                </section>

                {/* ── Get in Touch CTA ─────────────────────────────────────────── */}
                <section className="bg-[#fafafa] border-t border-[#f1f3f4] py-20">
                    <div className="max-w-7xl mx-auto px-6">
                        <div className="flex flex-col md:flex-row items-center justify-between gap-12 bg-white border border-[#e8eaed] rounded-[32px] p-8 md:p-16">
                            <div className="max-w-xl text-center md:text-left">
                                <div className="w-12 h-12 bg-[#e8f0fe] rounded-2xl flex items-center justify-center text-[#1a73e8] mb-6 mx-auto md:mx-0">
                                    <Mail size={24} />
                                </div>
                                <h2 className="text-3xl font-light text-[#121317] mb-4">Get in touch</h2>
                                <p className="text-[#5f6368] text-lg leading-relaxed">
                                    Questions, feedback, feature requests, or bug reports — our team responds
                                    to all inquiries within 48 business hours.
                                </p>
                            </div>
                            <div className="flex flex-col items-center gap-4 shrink-0">
                                <Link
                                    to="/contact"
                                    className="flex items-center gap-2 px-6 py-2.5 bg-[#1a73e8] text-white rounded-full font-medium hover:bg-[#1557b0] transition-colors shadow-sm no-underline"
                                >
                                    Contact Us
                                </Link>
                                <div className="flex flex-wrap gap-3 justify-center">
                                    {[
                                        { to: '/faq', label: 'Browse FAQs' },
                                        { to: '/features', label: 'All Features' },
                                        { to: '/tools', label: 'All Tools' },
                                    ].map(l => (
                                        <Link
                                            key={l.to}
                                            to={l.to}
                                            className="text-sm font-medium text-[#5f6368] hover:text-[#121317] px-4 py-2 bg-[#f8f9fa] rounded-full border border-[#dadce0] transition-colors no-underline"
                                        >
                                            {l.label}
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

            </div>
        </>
    );
};

export default AboutPage;
